import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";

import type {
  Result,
  SearchInputListResponse,
  SearchRequest,
  SearchResponse,
} from "@tsmono/inspect-common/types";

import type { SearchPanelApi, SearchResultScope, SearchType } from "./types";

export const DEFAULT_RECENT_SEARCH_COUNT = 20;

export const searchQueryKeys = {
  searches: ({
    searchType,
    count = DEFAULT_RECENT_SEARCH_COUNT,
  }: {
    searchType: SearchType;
    count?: number;
  }): readonly ["searches", SearchType, number] => [
    "searches",
    searchType,
    count,
  ],
  cachedResult: ({
    cacheKey,
    scope,
    searchId,
  }: {
    cacheKey: string | null;
    scope: SearchResultScope;
    searchId: string | null;
  }): readonly [
    "search-result",
    string | null,
    SearchResultScope,
    string | null,
  ] => ["search-result", cacheKey, scope, searchId],
};

export const useSearches = ({
  api,
  searchType,
  count = DEFAULT_RECENT_SEARCH_COUNT,
}: {
  api: SearchPanelApi;
  searchType: SearchType;
  count?: number;
}) => {
  return useQuery<SearchInputListResponse, Error>({
    queryKey: searchQueryKeys.searches({ searchType, count }),
    queryFn: () => api.listRecentSearches(searchType, count),
    staleTime: 60 * 1000,
  });
};

export const useCreateSearch = ({
  api,
}: {
  api: SearchPanelApi;
}): UseMutationResult<SearchResponse, Error, SearchRequest> => {
  const queryClient = useQueryClient();
  return useMutation<SearchResponse, Error, SearchRequest>({
    mutationFn: (request) => api.createSearch(request),
    onSuccess: (_response, request) => {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      queryClient.invalidateQueries({
        queryKey: searchQueryKeys.searches({ searchType: request.type }),
      });
    },
  });
};

export const useCachedSearchResult = ({
  api,
  scope,
  searchId,
}: {
  api: SearchPanelApi | null;
  scope: SearchResultScope;
  searchId: string | null;
}) => {
  return useQuery<Result | null, Error>({
    queryKey: searchQueryKeys.cachedResult({
      cacheKey: api?.cacheKey ?? null,
      scope,
      searchId,
    }),
    queryFn:
      api && searchId ? () => api.getCachedResult(searchId, scope) : skipToken,
    staleTime: Infinity,
  });
};
